import type {
  Client, FAQ, MenuItem, Metric, Partner, Project, Service, SiteConfig, User,
} from './types';

export type CollectionRow = Record<string, string | number | boolean | null | undefined>;
export type ApiErrorPayload = { error?: string; code?: string; requestId?: string };

function normalizeSiteResponse(value: unknown): SiteConfig {
  const data = value && typeof value === 'object' ? value as Partial<SiteConfig> : {};
  return {
    settings: data.settings && typeof data.settings === 'object' ? data.settings : {},
    metrics: Array.isArray(data.metrics) ? data.metrics : [],
    menu: Array.isArray(data.menu) ? data.menu : [],
    faqs: Array.isArray(data.faqs) ? data.faqs : [],
    clients: Array.isArray(data.clients) ? data.clients : [],
    partners: Array.isArray(data.partners) ? data.partners : [],
  };
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  const data = await response.json().catch(() => ({} as ApiErrorPayload));
  if (!response.ok) {
    const error = data as ApiErrorPayload;
    throw new Error(error.error || `Falha HTTP ${response.status}.`);
  }
  return data as T;
}

export interface AdminStats {
  user: User;
  counts: {
    services: number;
    activeServices: number;
    projects: number;
    activeProjects: number;
    clients: number;
    partners: number;
    subscribers: number;
    admins: number;
    activeSessions: number;
    faqs: number;
    menuItems: number;
    settings: number;
  };
  messagesByStatus: { new: number; read: number; closed: number; total: number };
  recentMessages: Array<{ id: string; name: string; email: string; subject: string; status: string; created_at: string }>;
  driver?: 'd1' | 'mysql';
  dbInfo?: { connected: boolean; driver: 'd1' | 'mysql'; latencyMs: number };
}

export interface ContactPayload {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  service?: string;
  budgetRange?: string;
  preferredContact?: 'email' | 'whatsapp' | 'phone' | '';
  subject: string;
  message: string;
  consent: boolean;
  source?: string;
  pageUrl?: string;
}

export const api = {
  services: () => request<Service[]>('/api/content/services'),
  projects: () => request<Project[]>('/api/content/projects'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/api/auth/login', {
      method: 'POST', body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<{ user: User }>('/api/auth/me'),
  stats: () => request<AdminStats>('/api/admin/stats'),
  users: () => request<User[]>('/api/admin/users'),
  createUser: (payload: Record<string, unknown>) =>
    request<{ ok: true; id: string }>('/api/admin/users', { method: 'POST', body: JSON.stringify(payload) }),
  updateUser: (id: string, payload: Record<string, unknown>) =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  removeUser: (id: string) =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  site: async () => normalizeSiteResponse(await request<unknown>('/api/content/site')),
  adminCollection: (name: string) => request<CollectionRow[]>(`/api/admin/${name}`),
  create: (name: string, payload: CollectionRow) =>
    request<{ ok: true; id: string }>(`/api/admin/${name}`, { method: 'POST', body: JSON.stringify(payload) }),
  update: (name: string, id: string, payload: CollectionRow) =>
    request<{ ok: true }>(`/api/admin/${name}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) }),
  remove: (name: string, id: string) =>
    request<{ ok: true }>(`/api/admin/${name}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  saveSetting: (key: string, value: string, meta?: { name?: string; comment?: string; activated?: boolean }) =>
    request<{ ok: true }>('/api/admin/settings', {
      method: 'POST', body: JSON.stringify({ key, value, ...meta }),
    }),
  removeSetting: (key: string) =>
    request<{ ok: true }>(`/api/admin/settings/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  messages: () => request<Array<Record<string, string | number | null>>>('/api/admin/messages'),
  updateMessage: (id: string, status: 'new' | 'read' | 'closed') =>
    request<{ ok: true }>('/api/admin/messages', { method: 'PATCH', body: JSON.stringify({ id, status }) }),
  contact: (payload: ContactPayload) =>
    request<{ ok: true }>('/api/contact', { method: 'POST', body: JSON.stringify(payload) }),
  newsletter: (email: string) =>
    request<{ ok: true }>('/api/newsletter', { method: 'POST', body: JSON.stringify({ email }) }),
  health: () => request<{
    ok: boolean;
    database: string;
    latencyMs: number;
    hyperdrive?: boolean;
    timestamp?: string;
    databases?: Array<{ driver: 'd1' | 'mysql'; available: boolean; latencyMs: number | null; error?: string }>;
  }>('/api/health'),
};
